import moment from 'moment'
import { AxiosError } from 'axios'
import { Expose, Transform, Type } from 'class-transformer'
import { Truck } from 'react-feather'

import { skuit } from 'src/services/skuit'
import { ActionDirection, WorkflowSequence } from 'src/modules/supply_chain/components/Workflow'
import { handleErrorMessage } from 'src/components/Notification'

import { Address } from './Address'
import { Base } from './Base'
import { Carrier } from './Carrier'
import { Client } from './Client'
import { Facility } from './Facility'
import { ParcelOrder } from './ParcelOrder'
import { Truckload } from './Truckload'

enum ParcelShipmentStatus {
  pending = 'Pending',
  shipped = 'Shipped',
  delivered = 'Delivered',
  canceled = 'Canceled',
}

export enum ParcelShipmentStatusEnum {
  pending = 'pending',
  shipped = 'shipped',
  delivered = 'delivered',
  canceled = 'canceled',
}

export class ParcelShipment extends Base {
  static type = 'parcelshipment'
  static typeDisplay = 'Shipment'
  static icon = Truck

  static Status = ParcelShipmentStatus

  type = ParcelShipment.type
  typeDisplay = ParcelShipment.typeDisplay
  icon = ParcelShipment.icon

  @Expose({ name: 'xid' })
  guid: string = ''

  name: string = ''

  @Expose({ name: 'tracking_number' })
  trackingNumber?: string

  @Expose({ name: 'internal_notes' })
  internalNotes?: string

  @Type(() => Client)
  client?: Client

  @Type(() => Facility)
  facility?: Facility

  @Type(() => ParcelOrder)
  order?: ParcelOrder

  @Type(() => Truckload)
  truckload?: Truckload

  consignee?: string

  @Type(() => Address)
  address?: Address

  @Type(() => Carrier)
  carrier?: Carrier

  status?: string

  @Expose({ name: 'shipped_on' })
  @Transform((value) => (value ? moment(value) : value))
  shippedOn?: moment.Moment

  @Expose({ name: 'delivered_on' })
  @Transform((value) => (value ? moment(value) : value))
  deliveredOn?: moment.Moment

  @Expose({ name: 'canceled_on' })
  @Transform((value) => (value ? moment(value) : value))
  canceledOn?: moment.Moment

  get workflowStatus() {
    if (this.status) {
      return this.status.charAt(0).toUpperCase() + this.status.slice(1)
    }
    return ''
  }

  get workflow(): WorkflowSequence {
    if (this.status === ParcelShipmentStatusEnum.canceled) {
      return [
        [ParcelShipmentStatus.pending, this.createdOn],
        [ParcelShipmentStatus.canceled, this.canceledOn],
      ]
    }
    return [
      [ParcelShipmentStatus.pending, this.createdOn],
      [ParcelShipmentStatus.shipped, this.shippedOn],
      [ParcelShipmentStatus.delivered, this.deliveredOn],
    ]
  }

  getWorkflowActions = (onAction?: () => void): { [status: string]: ActionDirection } => {
    return {
      [ParcelShipmentStatus.pending]: {
        forward: [
          {
            label: 'Ship',
            onClick: ({ complete }) => {
              skuit.sc.parcel.shipments
                .patch(this.guid, { status: 'shipped' })
                .then(() => onAction?.())
                .catch((error: AxiosError) =>
                  handleErrorMessage(error, 'updat', 'Parcel Shipment')
                )
                .finally(() => complete())
            },
          },
        ],
        cancel: [
          {
            label: 'Cancel',
            onClick: ({ complete }) => {
              skuit.sc.parcel.shipments
                .patch(this.guid, { status: 'canceled' })
                .then(() => onAction?.())
                .catch((error: AxiosError) =>
                  handleErrorMessage(error, 'updat', 'Parcel Shipment')
                )
                .finally(() => complete())
            },
          },
        ],
      },
      [ParcelShipmentStatus.shipped]: {
        forward: [
          {
            label: 'Deliver',
            onClick: ({ complete }) => {
              skuit.sc.parcel.shipments
                .patch(this.guid, { status: 'delivered' })
                .then(() => onAction?.())
                .catch((error: AxiosError) =>
                  handleErrorMessage(error, 'updat', 'Parcel Shipment')
                )
                .finally(() => complete())
            },
          },
        ],
        backward: [
          {
            label: 'Unship',
            onClick: ({ complete }) => {
              skuit.sc.parcel.shipments
                .patch(this.guid, { status: 'pending' })
                .then(() => onAction?.())
                .catch((error: AxiosError) =>
                  handleErrorMessage(error, 'updat', 'Parcel Shipment')
                )
                .finally(() => complete())
            },
          },
        ],
      },
      [ParcelShipmentStatus.delivered]: {
        backward: [
          {
            label: 'Undeliver',
            onClick: ({ complete }) => {
              skuit.sc.parcel.shipments
                .patch(this.guid, { status: 'shipped' })
                .then(() => onAction?.())
                .catch((error: AxiosError) =>
                  handleErrorMessage(error, 'updat', 'Parcel Shipment')
                )
                .finally(() => complete())
            },
          },
        ],
      },
      [ParcelShipmentStatus.canceled]: {
        backward: [
          {
            label: 'Uncancel',
            onClick: ({ complete }) => {
              skuit.sc.parcel.shipments
                .patch(this.guid, { status: 'pending' })
                .then(() => onAction?.())
                .catch((error: AxiosError) =>
                  handleErrorMessage(error, 'updat', 'Parcel Shipment')
                )
                .finally(() => complete())
            },
          },
        ],
      },
    }
  }
}
